import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Plus, Trash2, Building2, Shield, Layers,
  Loader2, FileSpreadsheet, Zap, X, Pencil, Check, Link2,
  StickyNote, ImageIcon, Upload, ArrowUp, ArrowDown
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DEPT_LABELS = {
  salud_local: "Salud Local",
  salud_internacional: "Salud Internacional",
  riesgos_generales: "Riesgos Generales",
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
};

export default function CotizacionesConfigTab() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [newAseguradora, setNewAseguradora] = useState("");
  const [newCobertura, setNewCobertura] = useState("");
  const [newCoberturaDept, setNewCoberturaDept] = useState("");
  const [newDeducible, setNewDeducible] = useState("");
  // Editing states
  const [editingAseg, setEditingAseg] = useState(null);
  const [editAsegValue, setEditAsegValue] = useState("");
  const [editingCob, setEditingCob] = useState(null);
  const [editCobName, setEditCobName] = useState("");
  const [editCobDept, setEditCobDept] = useState("");
  const [editingDed, setEditingDed] = useState(null);
  const [editDedValue, setEditDedValue] = useState("");
  // Plans
  const [newPlanName, setNewPlanName] = useState("");
  const [newPlanAseg, setNewPlanAseg] = useState("");
  const [newPlanDept, setNewPlanDept] = useState("");
  const [newPlanRamo, setNewPlanRamo] = useState("");
  const [editingPlan, setEditingPlan] = useState(null);
  const [editPlanName, setEditPlanName] = useState("");
  const [editPlanAseg, setEditPlanAseg] = useState("");
  const [editPlanDept, setEditPlanDept] = useState("");
  const [editPlanRamo, setEditPlanRamo] = useState("");

  // === OBSERVACIONES IMPORTANTES ===
  const [obsDept, setObsDept] = useState("salud_local");
  const [newObs, setNewObs] = useState("");
  const [editingObsIdx, setEditingObsIdx] = useState(null);
  const [editObsValue, setEditObsValue] = useState("");

  // === LOGOS DE ASEGURADORAS ===
  const [uploadingLogo, setUploadingLogo] = useState(null);  // nombre aseguradora en upload

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/cotizaciones/admin-config`, getAuthHeaders());
      setConfig(res.data);
    } catch (err) {
      toast.error("Error cargando configuración");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  // === ASEGURADORAS ===
  const handleAddAseguradora = async () => {
    const nombre = newAseguradora.trim();
    if (!nombre) return;
    try {
      await axios.post(`${API}/cotizaciones/admin-config/aseguradoras`, { nombre }, getAuthHeaders());
      toast.success(`Aseguradora "${nombre}" agregada`);
      setNewAseguradora("");
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al agregar aseguradora");
    }
  };

  const handleRemoveAseguradora = async (nombre) => {
    if (!window.confirm(`¿Eliminar "${nombre}" del catálogo?`)) return;
    try {
      await axios.delete(`${API}/cotizaciones/admin-config/aseguradoras/${encodeURIComponent(nombre)}`, getAuthHeaders());
      toast.success(`Aseguradora "${nombre}" eliminada`);
      fetchConfig();
    } catch (err) {
      toast.error("Error al eliminar aseguradora");
    }
  };

  const handleEditAseguradora = async (oldName) => {
    const newName = editAsegValue.trim();
    if (!newName || newName === oldName) { setEditingAseg(null); return; }
    const updated = (config.aseguradoras || []).map(a => a === oldName ? newName : a);
    try {
      await axios.put(`${API}/cotizaciones/admin-config`, { aseguradoras: updated }, getAuthHeaders());
      toast.success(`Aseguradora renombrada a "${newName}"`);
      setEditingAseg(null);
      fetchConfig();
    } catch (err) {
      toast.error("Error al editar aseguradora");
    }
  };

  // === OBSERVACIONES IMPORTANTES handlers ===
  const obsList = (config?.observaciones_importantes || {})[obsDept] || [];

  const saveObsList = async (nextList) => {
    const nextAll = { ...(config?.observaciones_importantes || {}), [obsDept]: nextList };
    try {
      await axios.put(
        `${API}/cotizaciones/admin-config`,
        { observaciones_importantes: nextAll },
        getAuthHeaders()
      );
      toast.success("Observaciones actualizadas");
      fetchConfig();
    } catch (err) {
      toast.error("Error al guardar observaciones");
    }
  };

  const handleAddObs = async () => {
    const txt = newObs.trim();
    if (!txt) return;
    setNewObs("");
    await saveObsList([...obsList, txt]);
  };

  const handleEditObs = async (idx) => {
    const txt = editObsValue.trim();
    if (!txt) { setEditingObsIdx(null); return; }
    const updated = obsList.map((o, i) => (i === idx ? txt : o));
    setEditingObsIdx(null);
    await saveObsList(updated);
  };

  const handleDeleteObs = async (idx) => {
    if (!window.confirm("¿Eliminar esta observación?")) return;
    await saveObsList(obsList.filter((_, i) => i !== idx));
  };

  const handleMoveObs = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= obsList.length) return;
    const updated = [...obsList];
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    await saveObsList(updated);
  };

  // === LOGOS DE ASEGURADORAS handlers ===
  const aseguradoraLogos = config?.aseguradora_logos || {};

  const handleUploadLogo = async (aseguradora, file) => {
    if (!file) return;
    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Formato no permitido (use PNG, JPG, JPEG o WebP)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("El archivo supera 5MB");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setUploadingLogo(aseguradora);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/cotizaciones/admin-config/aseguradoras/${encodeURIComponent(aseguradora)}/logo`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      toast.success(`Logo de "${aseguradora}" subido`);
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir logo");
    } finally {
      setUploadingLogo(null);
    }
  };

  const handleDeleteLogo = async (aseguradora) => {
    if (!window.confirm(`¿Eliminar el logo de "${aseguradora}"?`)) return;
    try {
      await axios.delete(
        `${API}/cotizaciones/admin-config/aseguradoras/${encodeURIComponent(aseguradora)}/logo`,
        getAuthHeaders()
      );
      toast.success("Logo eliminado");
      fetchConfig();
    } catch (err) {
      toast.error("Error al eliminar logo");
    }
  };

  // === COBERTURAS ===
  const handleAddCobertura = async () => {
    const nombre = newCobertura.trim();
    if (!nombre) return;
    try {
      await axios.post(`${API}/cotizaciones/admin-config/coberturas`, { nombre, departamento: newCoberturaDept }, getAuthHeaders());
      toast.success(`Cobertura "${nombre}" agregada`);
      setNewCobertura("");
      setNewCoberturaDept("");
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al agregar cobertura");
    }
  };

  const handleRemoveCobertura = async (cobertura) => {
    const updated = (config.coberturas_catalogo || []).filter(
      c => !(c.nombre === cobertura.nombre && c.departamento === cobertura.departamento)
    );
    try {
      await axios.put(`${API}/cotizaciones/admin-config`, { coberturas_catalogo: updated }, getAuthHeaders());
      toast.success("Cobertura eliminada");
      fetchConfig();
    } catch (err) {
      toast.error("Error al eliminar cobertura");
    }
  };

  const handleEditCobertura = async (index) => {
    const name = editCobName.trim();
    if (!name) { setEditingCob(null); return; }
    const updated = [...(config.coberturas_catalogo || [])];
    updated[index] = { ...updated[index], nombre: name, departamento: editCobDept || updated[index].departamento };
    try {
      await axios.put(`${API}/cotizaciones/admin-config`, { coberturas_catalogo: updated }, getAuthHeaders());
      toast.success("Cobertura actualizada");
      setEditingCob(null);
      fetchConfig();
    } catch (err) {
      toast.error("Error al editar cobertura");
    }
  };

  // === DEDUCIBLES ===
  const handleAddDeducible = async () => {
    const val = newDeducible.trim();
    if (!val) return;
    const updated = [...(config.deducibles_comunes || []), val];
    try {
      await axios.put(`${API}/cotizaciones/admin-config`, { deducibles_comunes: updated }, getAuthHeaders());
      toast.success(`Deducible "${val}" agregado`);
      setNewDeducible("");
      fetchConfig();
    } catch (err) {
      toast.error("Error al agregar deducible");
    }
  };

  const handleRemoveDeducible = async (val) => {
    const updated = (config.deducibles_comunes || []).filter(d => d !== val);
    try {
      await axios.put(`${API}/cotizaciones/admin-config`, { deducibles_comunes: updated }, getAuthHeaders());
      toast.success("Deducible eliminado");
      fetchConfig();
    } catch (err) {
      toast.error("Error al eliminar deducible");
    }
  };

  const handleEditDeducible = async (oldVal) => {
    const newVal = editDedValue.trim();
    if (!newVal || newVal === oldVal) { setEditingDed(null); return; }
    const updated = (config.deducibles_comunes || []).map(d => d === oldVal ? newVal : d);
    try {
      await axios.put(`${API}/cotizaciones/admin-config`, { deducibles_comunes: updated }, getAuthHeaders());
      toast.success("Deducible actualizado");
      setEditingDed(null);
      fetchConfig();
    } catch (err) {
      toast.error("Error al editar deducible");
    }
  };

  // === PLANES ===
  const handleAddPlan = async () => {
    const nombre = newPlanName.trim();
    if (!nombre) return;
    try {
      await axios.post(`${API}/cotizaciones/admin-config/planes`, {
        nombre, aseguradora: newPlanAseg, departamento: newPlanDept, ramo: newPlanRamo
      }, getAuthHeaders());
      toast.success(`Plan "${nombre}" agregado`);
      setNewPlanName(""); setNewPlanAseg(""); setNewPlanDept(""); setNewPlanRamo("");
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al agregar plan");
    }
  };

  const handleRemovePlan = async (plan, index) => {
    const updated = (config.planes_catalogo || []).filter((_, i) => i !== index);
    try {
      await axios.put(`${API}/cotizaciones/admin-config`, { planes_catalogo: updated }, getAuthHeaders());
      toast.success("Plan eliminado");
      fetchConfig();
    } catch (err) {
      toast.error("Error al eliminar plan");
    }
  };

  const handleEditPlan = async (index) => {
    const name = editPlanName.trim();
    if (!name) { setEditingPlan(null); return; }
    const updated = [...(config.planes_catalogo || [])];
    updated[index] = { ...updated[index], nombre: name, aseguradora: editPlanAseg || updated[index].aseguradora, departamento: editPlanDept || updated[index].departamento, ramo: editPlanRamo !== undefined ? editPlanRamo : (updated[index].ramo || "") };
    try {
      await axios.put(`${API}/cotizaciones/admin-config`, { planes_catalogo: updated }, getAuthHeaders());
      toast.success("Plan actualizado");
      setEditingPlan(null);
      fetchConfig();
    } catch (err) {
      toast.error("Error al editar plan");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  const aseguradoras = config?.aseguradoras || [];
  const coberturas = config?.coberturas_catalogo || [];
  const deducibles = config?.deducibles_comunes || [];
  const planes = config?.planes_catalogo || [];
  const aiCoberturas = coberturas.filter(c => c.source === "ai_extraction");
  const aiPlanes = planes.filter(p => p.source === "ai_extraction");

  return (
    <div className="space-y-6" data-testid="cotizaciones-config-tab">
      {/* Aseguradoras */}
      <Card className="bg-slate-900 border-slate-800 rounded-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-slate-100 flex items-center gap-2 text-lg">
            <Building2 className="w-5 h-5 text-emerald-400" />
            Aseguradoras
          </CardTitle>
          <p className="text-sm text-slate-500">Haga doble clic en una aseguradora para editarla</p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            {aseguradoras.map((a) => (
              editingAseg === a ? (
                <div key={a} className="flex items-center gap-1">
                  <Input
                    value={editAsegValue}
                    onChange={(e) => setEditAsegValue(e.target.value)}
                    className="h-8 w-48 bg-slate-800 border-emerald-600 text-slate-100 rounded-lg text-sm"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleEditAseguradora(a);
                      if (e.key === "Escape") setEditingAseg(null);
                    }}
                    data-testid="edit-aseguradora-input"
                  />
                  <Button size="icon" className="w-7 h-7 bg-emerald-600" onClick={() => handleEditAseguradora(a)}>
                    <Check className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="w-7 h-7" onClick={() => setEditingAseg(null)}>
                    <X className="w-3.5 h-3.5 text-slate-400" />
                  </Button>
                </div>
              ) : (
                <Badge
                  key={a}
                  className="bg-slate-800 text-slate-200 border border-slate-700 rounded-lg px-3 py-1.5 gap-1.5 hover:border-slate-600 transition-colors group cursor-pointer"
                  onDoubleClick={() => { setEditingAseg(a); setEditAsegValue(a); }}
                  data-testid={`aseguradora-badge-${a.replace(/\s+/g, '-')}`}
                >
                  {a}
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditingAseg(a); setEditAsegValue(a); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Editar"
                  >
                    <Pencil className="w-3 h-3 text-slate-500 hover:text-emerald-400" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRemoveAseguradora(a); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-aseguradora-${a.replace(/\s+/g, '-')}`}
                  >
                    <X className="w-3.5 h-3.5 text-slate-500 hover:text-red-400" />
                  </button>
                </Badge>
              )
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={newAseguradora}
              onChange={(e) => setNewAseguradora(e.target.value)}
              placeholder="Nueva aseguradora..."
              className="bg-slate-800 border-slate-700 text-slate-100 rounded-xl flex-1"
              onKeyDown={(e) => e.key === "Enter" && handleAddAseguradora()}
              data-testid="new-aseguradora-input"
            />
            <Button
              onClick={handleAddAseguradora}
              disabled={!newAseguradora.trim()}
              className="bg-emerald-600 hover:bg-emerald-500 rounded-xl gap-1"
              data-testid="add-aseguradora-btn"
            >
              <Plus className="w-4 h-4" /> Agregar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Coberturas */}
      <Card className="bg-slate-900 border-slate-800 rounded-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-slate-100 flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5 text-sky-400" />
            Coberturas
            {aiCoberturas.length > 0 && (
              <Badge className="bg-sky-900/50 text-sky-300 text-xs ml-2 gap-1">
                <Zap className="w-3 h-3" /> {aiCoberturas.length} detectadas por IA
              </Badge>
            )}
          </CardTitle>
          <p className="text-sm text-slate-500">Haga clic en el lápiz para editar nombre o departamento</p>
        </CardHeader>
        <CardContent>
          {coberturas.length > 0 ? (
            <div className="space-y-2 mb-4 max-h-72 overflow-y-auto pr-1">
              {coberturas.map((c, i) => (
                editingCob === i ? (
                  <div
                    key={`${c.nombre}-${i}`}
                    className="flex items-center gap-2 p-2.5 rounded-xl border border-sky-700 bg-slate-800/80"
                  >
                    <Input
                      value={editCobName}
                      onChange={(e) => setEditCobName(e.target.value)}
                      className="h-8 bg-slate-900 border-slate-700 text-slate-100 rounded-lg text-sm flex-1"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleEditCobertura(i);
                        if (e.key === "Escape") setEditingCob(null);
                      }}
                      data-testid="edit-cobertura-name-input"
                    />
                    <Select value={editCobDept} onValueChange={setEditCobDept}>
                      <SelectTrigger className="w-40 h-8 bg-slate-900 border-slate-700 text-slate-100 rounded-lg text-xs" data-testid="edit-cobertura-dept">
                        <SelectValue placeholder="Depto" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        {Object.entries(DEPT_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="icon" className="w-7 h-7 bg-sky-600 shrink-0" onClick={() => handleEditCobertura(i)}>
                      <Check className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="w-7 h-7 shrink-0" onClick={() => setEditingCob(null)}>
                      <X className="w-3.5 h-3.5 text-slate-400" />
                    </Button>
                  </div>
                ) : (
                  <div
                    key={`${c.nombre}-${c.departamento}-${i}`}
                    className={`flex items-center justify-between p-2.5 rounded-xl border ${
                      c.source === "ai_extraction"
                        ? "bg-sky-950/30 border-sky-900/40"
                        : "bg-slate-800/50 border-slate-800"
                    }`}
                    data-testid={`cobertura-item-${i}`}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-sm text-slate-200 truncate">{c.nombre}</span>
                      {c.departamento && c.departamento !== "todos" && (
                        <Badge className="bg-slate-700/50 text-slate-400 text-[10px] rounded shrink-0">
                          {DEPT_LABELS[c.departamento] || c.departamento}
                        </Badge>
                      )}
                      {c.source === "ai_extraction" && (
                        <Badge className="bg-sky-900/40 text-sky-400 text-[10px] rounded shrink-0 gap-0.5">
                          <Zap className="w-2.5 h-2.5" /> IA
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-7 h-7 text-slate-600 hover:text-sky-400"
                        onClick={() => { setEditingCob(i); setEditCobName(c.nombre); setEditCobDept(c.departamento || ""); }}
                        data-testid={`edit-cobertura-${i}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-7 h-7 text-slate-600 hover:text-red-400"
                        onClick={() => handleRemoveCobertura(c)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                )
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 mb-4">No hay coberturas en el catálogo. Suba documentos con la IA o agregue manualmente.</p>
          )}
          <div className="flex gap-2">
            <Input
              value={newCobertura}
              onChange={(e) => setNewCobertura(e.target.value)}
              placeholder="Nombre de cobertura..."
              className="bg-slate-800 border-slate-700 text-slate-100 rounded-xl flex-1"
              onKeyDown={(e) => e.key === "Enter" && handleAddCobertura()}
              data-testid="new-cobertura-input"
            />
            <Select value={newCoberturaDept} onValueChange={setNewCoberturaDept}>
              <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-slate-100 rounded-xl" data-testid="new-cobertura-dept">
                <SelectValue placeholder="Departamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {Object.entries(DEPT_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleAddCobertura}
              disabled={!newCobertura.trim()}
              className="bg-sky-600 hover:bg-sky-500 rounded-xl gap-1"
              data-testid="add-cobertura-btn"
            >
              <Plus className="w-4 h-4" /> Agregar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Deducibles */}
      <Card className="bg-slate-900 border-slate-800 rounded-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-slate-100 flex items-center gap-2 text-lg">
            <FileSpreadsheet className="w-5 h-5 text-amber-400" />
            Deducibles Comunes
          </CardTitle>
          <p className="text-sm text-slate-500">Haga doble clic en un deducible para editarlo</p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            {deducibles.map((d) => (
              editingDed === d ? (
                <div key={d} className="flex items-center gap-1">
                  <Input value={editDedValue} onChange={(e) => setEditDedValue(e.target.value)} className="h-8 w-40 bg-slate-800 border-amber-600 text-slate-100 rounded-lg text-sm" autoFocus onKeyDown={(e) => { if (e.key === "Enter") handleEditDeducible(d); if (e.key === "Escape") setEditingDed(null); }} data-testid="edit-deducible-input" />
                  <Button size="icon" className="w-7 h-7 bg-amber-600" onClick={() => handleEditDeducible(d)}><Check className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="w-7 h-7" onClick={() => setEditingDed(null)}><X className="w-3.5 h-3.5 text-slate-400" /></Button>
                </div>
              ) : (
                <Badge key={d} className="bg-slate-800 text-slate-200 border border-slate-700 rounded-lg px-3 py-1.5 gap-1.5 hover:border-slate-600 transition-colors group cursor-pointer" onDoubleClick={() => { setEditingDed(d); setEditDedValue(d); }} data-testid={`deducible-badge-${d.replace(/[^a-zA-Z0-9]/g, '-')}`}>
                  {d}
                  <button onClick={(e) => { e.stopPropagation(); setEditingDed(d); setEditDedValue(d); }} className="opacity-0 group-hover:opacity-100 transition-opacity" title="Editar"><Pencil className="w-3 h-3 text-slate-500 hover:text-amber-400" /></button>
                  <button onClick={(e) => { e.stopPropagation(); handleRemoveDeducible(d); }} className="opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3.5 h-3.5 text-slate-500 hover:text-red-400" /></button>
                </Badge>
              )
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newDeducible} onChange={(e) => setNewDeducible(e.target.value)} placeholder="Ej: RD$ 2,500" className="bg-slate-800 border-slate-700 text-slate-100 rounded-xl flex-1" onKeyDown={(e) => e.key === "Enter" && handleAddDeducible()} data-testid="new-deducible-input" />
            <Button onClick={handleAddDeducible} disabled={!newDeducible.trim()} className="bg-amber-600 hover:bg-amber-500 rounded-xl gap-1" data-testid="add-deducible-btn"><Plus className="w-4 h-4" /> Agregar</Button>
          </div>
        </CardContent>
      </Card>

      {/* Planes */}
      <Card className="bg-slate-900 border-slate-800 rounded-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-slate-100 flex items-center gap-2 text-lg">
            <Layers className="w-5 h-5 text-violet-400" />
            Planes
            {aiPlanes.length > 0 && (
              <Badge className="bg-violet-900/50 text-violet-300 text-xs ml-2 gap-1">
                <Zap className="w-3 h-3" /> {aiPlanes.length} detectados por IA
              </Badge>
            )}
          </CardTitle>
          <p className="text-sm text-slate-500">Planes de aseguradoras con coberturas enlazadas. La IA los detecta automáticamente.</p>
        </CardHeader>
        <CardContent>
          {planes.length > 0 ? (
            <div className="space-y-2 mb-4 max-h-72 overflow-y-auto pr-1">
              {planes.map((p, i) => (
                editingPlan === i ? (
                  <div key={i} className="flex flex-wrap items-center gap-2 p-2.5 rounded-xl border border-violet-700 bg-slate-800/80">
                    <Input value={editPlanName} onChange={(e) => setEditPlanName(e.target.value)} className="h-8 bg-slate-900 border-slate-700 text-slate-100 rounded-lg text-sm flex-1 min-w-[140px]" autoFocus onKeyDown={(e) => { if (e.key === "Enter") handleEditPlan(i); if (e.key === "Escape") setEditingPlan(null); }} data-testid="edit-plan-name" />
                    <Input value={editPlanAseg} onChange={(e) => setEditPlanAseg(e.target.value)} placeholder="Aseguradora" className="h-8 w-36 bg-slate-900 border-slate-700 text-slate-100 rounded-lg text-xs" />
                    <Select value={editPlanDept} onValueChange={setEditPlanDept}>
                      <SelectTrigger className="w-36 h-8 bg-slate-900 border-slate-700 text-slate-100 rounded-lg text-xs"><SelectValue placeholder="Depto" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        {Object.entries(DEPT_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Input value={editPlanRamo} onChange={(e) => setEditPlanRamo(e.target.value)} placeholder="Ramo" className="h-8 w-32 bg-slate-900 border-slate-700 text-slate-100 rounded-lg text-xs" data-testid="edit-plan-ramo" />
                    <Button size="icon" className="w-7 h-7 bg-violet-600 shrink-0" onClick={() => handleEditPlan(i)}><Check className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="w-7 h-7 shrink-0" onClick={() => setEditingPlan(null)}><X className="w-3.5 h-3.5 text-slate-400" /></Button>
                  </div>
                ) : (
                  <div key={i} className={`flex items-center justify-between p-2.5 rounded-xl border ${p.source === "ai_extraction" ? "bg-violet-950/20 border-violet-900/40" : "bg-slate-800/50 border-slate-800"}`} data-testid={`plan-item-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-slate-200 font-medium truncate">{p.nombre}</span>
                        {p.aseguradora && <Badge className="bg-slate-700/50 text-slate-400 text-[10px] rounded shrink-0">{p.aseguradora}</Badge>}
                        {p.departamento && p.departamento !== "todos" && <Badge className="bg-slate-700/50 text-slate-500 text-[10px] rounded shrink-0">{DEPT_LABELS[p.departamento] || p.departamento}</Badge>}
                        {p.ramo && <Badge className="bg-amber-900/40 text-amber-400 text-[10px] rounded shrink-0">{p.ramo}</Badge>}
                        {p.source === "ai_extraction" && <Badge className="bg-violet-900/40 text-violet-400 text-[10px] rounded shrink-0 gap-0.5"><Zap className="w-2.5 h-2.5" /> IA</Badge>}
                      </div>
                      {p.coberturas?.length > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <Link2 className="w-3 h-3 text-slate-600" />
                          <span className="text-[10px] text-slate-500 truncate">{p.coberturas.slice(0, 4).join(", ")}{p.coberturas.length > 4 ? ` +${p.coberturas.length - 4} más` : ""}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-slate-600 hover:text-violet-400" onClick={() => { setEditingPlan(i); setEditPlanName(p.nombre); setEditPlanAseg(p.aseguradora || ""); setEditPlanDept(p.departamento || ""); setEditPlanRamo(p.ramo || ""); }}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-slate-600 hover:text-red-400" onClick={() => handleRemovePlan(p, i)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                )
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 mb-4">No hay planes en el catálogo. Suba documentos con la IA o agregue manualmente.</p>
          )}
          <div className="flex gap-2 flex-wrap">
            <Input value={newPlanName} onChange={(e) => setNewPlanName(e.target.value)} placeholder="Nombre del plan..." className="bg-slate-800 border-slate-700 text-slate-100 rounded-xl flex-1 min-w-[140px]" onKeyDown={(e) => e.key === "Enter" && handleAddPlan()} data-testid="new-plan-name-input" />
            <Input value={newPlanAseg} onChange={(e) => setNewPlanAseg(e.target.value)} placeholder="Aseguradora" className="bg-slate-800 border-slate-700 text-slate-100 rounded-xl w-36" data-testid="new-plan-aseg-input" />
            <Select value={newPlanDept} onValueChange={setNewPlanDept}>
              <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-slate-100 rounded-xl" data-testid="new-plan-dept"><SelectValue placeholder="Departamento" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {Object.entries(DEPT_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
              </SelectContent>
            </Select>
            <Input value={newPlanRamo} onChange={(e) => setNewPlanRamo(e.target.value)} placeholder="Ramo" className="bg-slate-800 border-slate-700 text-slate-100 rounded-xl w-32" data-testid="new-plan-ramo-input" />
            <Button onClick={handleAddPlan} disabled={!newPlanName.trim()} className="bg-violet-600 hover:bg-violet-500 rounded-xl gap-1" data-testid="add-plan-btn"><Plus className="w-4 h-4" /> Agregar</Button>
          </div>
        </CardContent>
      </Card>

      {/* === OBSERVACIONES IMPORTANTES === */}
      <Card className="bg-slate-900 border-slate-800 rounded-2xl" data-testid="config-observaciones-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between gap-3 text-slate-100">
            <span className="flex items-center gap-2 text-base">
              <StickyNote className="w-5 h-5 text-amber-400" />
              Observaciones Importantes
              <Badge variant="outline" className="bg-amber-500/10 text-amber-300 border-amber-700/50 ml-1">
                {obsList.length}
              </Badge>
            </span>
            <Select value={obsDept} onValueChange={setObsDept}>
              <SelectTrigger className="w-52 bg-slate-800 border-slate-700 text-slate-100 rounded-xl" data-testid="obs-dept-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DEPT_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardTitle>
          <p className="text-xs text-slate-400 pt-1">
            Estas notas aparecerán al pie de los reportes Excel y PDF generados para este departamento.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            {obsList.length === 0 && (
              <p className="text-sm text-slate-500 italic">Sin observaciones. Agrega la primera abajo.</p>
            )}
            {obsList.map((obs, idx) => (
              <div key={idx} className="flex items-start gap-2 p-3 bg-slate-800/50 border border-slate-700/50 rounded-xl group hover:border-slate-600 transition-colors" data-testid={`obs-item-${idx}`}>
                <span className="text-amber-400 mt-0.5 font-semibold select-none">•</span>
                {editingObsIdx === idx ? (
                  <>
                    <Input
                      value={editObsValue}
                      onChange={(e) => setEditObsValue(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleEditObs(idx)}
                      autoFocus
                      className="flex-1 bg-slate-900 border-slate-600 text-slate-100"
                      data-testid={`obs-edit-input-${idx}`}
                    />
                    <Button size="sm" onClick={() => handleEditObs(idx)} className="bg-emerald-600 hover:bg-emerald-500" data-testid={`obs-edit-save-${idx}`}>
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingObsIdx(null)} className="text-slate-400">
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="flex-1 text-sm text-slate-200 leading-relaxed">{obs}</p>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="sm" variant="ghost" onClick={() => handleMoveObs(idx, -1)} disabled={idx === 0} className="h-8 w-8 p-0 text-slate-400 hover:text-slate-100">
                        <ArrowUp className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleMoveObs(idx, 1)} disabled={idx === obsList.length - 1} className="h-8 w-8 p-0 text-slate-400 hover:text-slate-100">
                        <ArrowDown className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEditingObsIdx(idx); setEditObsValue(obs); }} className="h-8 w-8 p-0 text-slate-400 hover:text-amber-400" data-testid={`obs-edit-btn-${idx}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteObs(idx)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-400" data-testid={`obs-delete-btn-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <Input
              value={newObs}
              onChange={(e) => setNewObs(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddObs()}
              placeholder="Ej: Tarifas expresadas en US$ Dólares Americanos"
              className="bg-slate-800 border-slate-700 text-slate-100 rounded-xl"
              data-testid="obs-new-input"
            />
            <Button
              onClick={handleAddObs}
              disabled={!newObs.trim()}
              className="bg-amber-600 hover:bg-amber-500 rounded-xl gap-1"
              data-testid="obs-add-btn"
            >
              <Plus className="w-4 h-4" /> Agregar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* === LOGOS DE ASEGURADORAS === */}
      <Card className="bg-slate-900 border-slate-800 rounded-2xl" data-testid="config-logos-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-slate-100 text-base">
            <ImageIcon className="w-5 h-5 text-blue-400" />
            Logos de Aseguradoras
            <Badge variant="outline" className="bg-blue-500/10 text-blue-300 border-blue-700/50 ml-1">
              {Object.keys(aseguradoraLogos).length} / {(config?.aseguradoras || []).length}
            </Badge>
          </CardTitle>
          <p className="text-xs text-slate-400 pt-1">
            Sube un logo (PNG, JPG, JPEG o WebP — máx 5MB) por aseguradora. Se utilizará en las plantillas Excel y PDF de cotizaciones.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(config?.aseguradoras || []).map((aseg) => {
              const hasLogo = Boolean(aseguradoraLogos[aseg]);
              const logoUrl = hasLogo
                ? `${API}/cotizaciones/admin-config/aseguradoras/${encodeURIComponent(aseg)}/logo?v=${Date.now()}`
                : null;
              const isUploading = uploadingLogo === aseg;
              return (
                <div key={aseg} className="flex items-center gap-3 p-3 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-slate-600 transition-colors" data-testid={`logo-row-${aseg}`}>
                  <div className="w-16 h-16 flex-shrink-0 bg-slate-900 border border-slate-700 rounded-lg flex items-center justify-center overflow-hidden">
                    {hasLogo ? (
                      <img src={logoUrl} alt={aseg} className="max-w-full max-h-full object-contain" />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-100 truncate">{aseg}</p>
                    <p className="text-xs text-slate-500">{hasLogo ? "Logo cargado" : "Sin logo"}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        className="hidden"
                        onChange={(e) => handleUploadLogo(aseg, e.target.files?.[0])}
                        data-testid={`logo-upload-input-${aseg}`}
                      />
                      <Button
                        asChild
                        size="sm"
                        variant="ghost"
                        disabled={isUploading}
                        className="h-8 w-8 p-0 text-slate-400 hover:text-blue-400"
                        data-testid={`logo-upload-btn-${aseg}`}
                      >
                        <span>
                          {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        </span>
                      </Button>
                    </label>
                    {hasLogo && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteLogo(aseg)}
                        className="h-8 w-8 p-0 text-slate-400 hover:text-red-400"
                        data-testid={`logo-delete-btn-${aseg}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            {(config?.aseguradoras || []).length === 0 && (
              <p className="text-sm text-slate-500 italic col-span-full">
                No hay aseguradoras en el catálogo. Agrégalas en la sección "Aseguradoras" más arriba.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
